/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import LoadingIndicator from "@atg/icf-react-components/components/LoadingIndicator";
import ITheme from "@atg/icf-react-components/themes/Theme";
import { FC, useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import { useLocation } from "react-router-dom";

import { getRouteConfig } from "../../../config/route.config";
import { PAGE_HEADER_HEIGHT } from "~/features/header/constants";

interface StyleProps {
  loaderStyle: "dark" | "light";
  loaderSize: "full" | "content";
}

const useStyles = createUseStyles<string, StyleProps, ITheme>((theme: ITheme) => ({
  pageLoader: {
    width: "100%",
    height: (props) => (props.loaderSize === "full" ? "100vh" : `calc(100vh - ${PAGE_HEADER_HEIGHT}px)`),
    background: (props) => (props.loaderStyle === "dark" ? theme.colors.baseContrast100 : theme.colors.baseContrast010),
    display: "flex",
    justifyContent: "center",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const RouteFallbackLoader: FC = () => {
  const location = useLocation();

  const { routeFallbackLoader } = getRouteConfig(location.pathname);

  const classes = useStyles({
    loaderStyle: routeFallbackLoader.style,
    loaderSize: routeFallbackLoader.size,
  });

  const [showIndicator, setShowIndicator] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowIndicator(true);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  if (!routeFallbackLoader.enabled) {
    return null;
  }

  return (
    <section className={classes.pageLoader} data-ta-locator="RouteFallbackLoader">
      <div className={classes.content}>{showIndicator && <LoadingIndicator size={45} />}</div>
    </section>
  );
};

export default RouteFallbackLoader;
